import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { LevelBadge, LEVELS, daysLabel } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import { Boxes, FileText, Bell, ArrowRight, Sparkles, Mic } from "lucide-react";

export default function Brief() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => { api.get("/brief").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="text-muted-foreground">Chargement du brief…</div>;

  const cards = filter === "all" ? data.cards : data.cards.filter((c) => c.level === filter);

  return (
    <div className="space-y-8">
      <div className="animate-fadeUp">
        <p className="text-muted-foreground text-sm">Bonjour {user?.name} 👋</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
          Voici ce qui mérite ton attention.
        </h1>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Biens mémorisés", value: data.stats.items, icon: Boxes },
          { label: "Documents", value: data.stats.documents, icon: FileText },
          { label: "Rappels actifs", value: data.stats.reminders, icon: Bell },
        ].map((s, i) => (
          <div key={i} data-testid={`brief-stat-${i}`} className="omy-glass rounded-2xl p-5">
            <s.icon size={18} className="text-primary mb-3" />
            <div className="text-2xl font-display font-semibold">{s.value}</div>
            <div className="text-xs text-muted-foreground">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <button data-testid="brief-filter-all" onClick={() => setFilter("all")}
          className={`text-xs px-3 py-1.5 rounded-full border ${filter === "all" ? "border-primary text-primary" : "border-white/10 text-muted-foreground"}`}>
          Tout ({data.cards.length})
        </button>
        {Object.entries(LEVELS).map(([k, v]) => (
          <button key={k} data-testid={`brief-filter-${k}`} onClick={() => setFilter(k)}
            className={`text-xs px-3 py-1.5 rounded-full border ${filter === k ? "border-primary text-primary" : "border-white/10 text-muted-foreground"}`}>
            {v.label} ({data.counts[k] || 0})
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {cards.length === 0 && <p className="text-muted-foreground text-sm">Rien à signaler ici. 🎉</p>}
        {cards.map((c, i) => (
          <div key={c.id} data-testid={`brief-card-${i}`}
            className="omy-glass rounded-2xl p-5 hover:border-primary/40 transition-all cursor-pointer"
            onClick={() => c.item_id && navigate(`/app/memoire/${c.item_id}`)}
            style={{ borderLeft: `3px solid ${(LEVELS[c.level] || LEVELS.savoir).color}` }}>
            <div className="flex items-start justify-between gap-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{c.category}</span>
              <LevelBadge level={c.level} testid={`brief-card-level-${i}`} />
            </div>
            <h3 className="font-display text-base font-medium mt-2">{c.title}</h3>
            <div className="flex items-center justify-between mt-3 text-sm text-muted-foreground">
              <span>{c.mileage_due ? `${c.mileage_due.toLocaleString("fr-FR")} km` : daysLabel(c.days_left)}</span>
              {c.note && <span className="text-xs truncate max-w-[50%]">{c.note}</span>}
            </div>
          </div>
        ))}
      </div>

      <div className="grid sm:grid-cols-[1fr_auto] gap-3">
        <button data-testid="brief-talk-omy-btn" onClick={() => navigate("/app/assistant?voice=1")}
          className="omy-glass omy-glow rounded-2xl p-5 flex items-center gap-4 hover:border-primary/50 transition-all text-left">
          <span className="w-12 h-12 rounded-full flex items-center justify-center pulse-ring shrink-0" style={{ background: "linear-gradient(135deg,#E05A36,#F59E0B)" }}>
            <Mic size={22} className="text-white" />
          </span>
          <span>
            <span className="block font-display font-medium">Parler à OMY</span>
            <span className="block text-xs text-muted-foreground">Pose ta question ou dicte une info — OMY te répond en vocal</span>
          </span>
        </button>
        <button data-testid="brief-ask-omy-btn" onClick={() => navigate("/app/assistant")}
          className="omy-glass rounded-2xl px-6 py-4 flex items-center justify-center gap-2 hover:border-primary/40 transition-all group">
          <Sparkles className="text-primary" size={18} />
          <span className="font-display font-medium">Écrire</span>
          <ArrowRight className="text-muted-foreground group-hover:translate-x-1 transition-transform" size={16} />
        </button>
      </div>
    </div>
  );
}
